import { StrictMode, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { AppProvider, useApp } from './lib/app-context';
import { DisabledModule, Shell } from './pages/Shell';
import { ConversationView, MessagesHome } from './pages/Messages';
import { ChatProvider } from './lib/chat-context';
import { startInstallWatch } from './lib/install';
import { lazyPage, PageBoundary, prefetchPages } from './lib/lazy-pages';
import './styles/tokens.css';
import './styles/app.css';

// Chats and the shell are the first screen after sign-in and stay in the main file; every other page is
// its own file, loaded when opened and prefetched in the background (lib/lazy-pages).
const LoginPage = lazyPage(() => import('./pages/AuthPages'), 'LoginPage');
const RegisterPage = lazyPage(() => import('./pages/AuthPages'), 'RegisterPage');
const ForgotPasswordPage = lazyPage(() => import('./pages/AccountPages'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyPage(() => import('./pages/AccountPages'), 'ResetPasswordPage');
const VerifyEmailPage = lazyPage(() => import('./pages/AccountPages'), 'VerifyEmailPage');
const OnboardingPage = lazyPage(() => import('./pages/OnboardingPage'), 'OnboardingPage');
const SettingsPage = lazyPage(() => import('./pages/SettingsPage'), 'SettingsPage');
const AdminPage = lazyPage(() => import('./pages/AdminPage'), 'AdminPage');
const SpacesPage = lazyPage(() => import('./pages/SpacesPage'), 'SpacesPage');
const StarredPage = lazyPage(() => import('./pages/StarredPage'), 'StarredPage');
const ChannelsPage = lazyPage(() => import('./pages/ChannelsPage'), 'ChannelsPage');
const PlannerPage = lazyPage(() => import('./pages/PlannerPage'), 'PlannerPage');
const LearnPage = lazyPage(() => import('./pages/LearnPage'), 'LearnPage');
const JoinEduPage = lazyPage(() => import('./pages/JoinEduPage'), 'JoinEduPage');
const JoinGroupPage = lazyPage(() => import('./pages/JoinGroupPage'), 'JoinGroupPage');
const JoinSpacePage = lazyPage(() => import('./pages/JoinSpacePage'), 'JoinSpacePage');
const CallsPage = lazyPage(() => import('./components/CallUi'), 'CallsPage');
const AiPage = lazyPage(() => import('./components/AssistantUi'), 'AiPage');
const LinkDevicePage = lazyPage(() => import('./components/QrLogin'), 'LinkDevicePage');

/** requireAuth: true = signed in only, false = signed out only, 'any' = both (email links). */
function Gate({ children, requireAuth }: { children: ReactNode; requireAuth: boolean | 'any' }) {
  const { me, loading, config, i18n } = useApp();
  const here = useLocation();
  if (loading) {
    return (
      <div className="auth-page" role="status">
        {i18n.t('app.loading')}
      </div>
    );
  }
  if (!config) {
    return (
      <div className="auth-page">
        <div className="card stack auth-card" role="alert">
          <p>{i18n.t('error.NETWORK')}</p>
          <button className="btn btn-primary" type="button" onClick={() => location.reload()}>
            {i18n.t('common.retry')}
          </button>
        </div>
      </div>
    );
  }
  if (requireAuth === 'any') return <>{children}</>;
  // A group invite or a QR sign-in link survives signing in: the login page returns there (fragment included).
  if (requireAuth && !me) {
    const keep = here.pathname === '/join' || here.pathname === '/link';
    return <Navigate to="/login" replace state={keep ? { from: `${here.pathname}${here.hash}` } : undefined} />;
  }
  if (!requireAuth && me) return <Navigate to={me.onboardingCompleted ? '/' : '/onboarding'} replace />;
  return <>{children}</>;
}

/** Route changes start at the top, like a page load (a long form would otherwise leave the next page scrolled). */
function ScrollReset() {
  const { pathname } = useLocation();
  // Block body: newer browsers return a Promise from scrollTo, which must not become the effect cleanup.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/** Messenger-first: land on chats when messaging is enabled. */
function HomeRedirect() {
  const { config } = useApp();
  return <Navigate to={config?.flags.messaging ? '/messages' : '/spaces'} replace />;
}

function LearnRoute() {
  const { config } = useApp();
  return config?.flags.learning_hub ? <LearnPage /> : <DisabledModule labelKey="nav.learn" />;
}

function CalendarRoute() {
  const { config } = useApp();
  return config?.flags.calendar || config?.flags.tasks ? <PlannerPage /> : <DisabledModule labelKey="nav.calendar" />;
}

function DiscoverRoute() {
  const { config } = useApp();
  return config?.flags.channels ? <ChannelsPage /> : <DisabledModule labelKey="nav.discover" />;
}

function MessagesRoute() {
  const { config } = useApp();
  return config?.flags.messaging ? <MessagesHome /> : <DisabledModule labelKey="nav.messages" />;
}

function ConversationRoute() {
  const { config } = useApp();
  return config?.flags.messaging ? <ConversationView /> : <DisabledModule labelKey="nav.messages" />;
}

function App() {
  const { pathname } = useLocation();
  return (
    <PageBoundary resetKey={pathname}>
      <Routes>
        <Route path="/login" element={<Gate requireAuth={false}><LoginPage /></Gate>} />
        <Route path="/register" element={<Gate requireAuth={false}><RegisterPage /></Gate>} />
        <Route path="/forgot-password" element={<Gate requireAuth={false}><ForgotPasswordPage /></Gate>} />
        <Route path="/reset-password" element={<Gate requireAuth="any"><ResetPasswordPage /></Gate>} />
        <Route path="/verify-email" element={<Gate requireAuth="any"><VerifyEmailPage /></Gate>} />
        <Route path="/onboarding" element={<Gate requireAuth><OnboardingPage /></Gate>} />
        <Route element={<Gate requireAuth><Shell /></Gate>}>
          <Route index element={<HomeRedirect />} />
          <Route path="spaces" element={<SpacesPage />} />
          <Route path="spaces/:id" element={<SpacesPage />} />
          <Route path="spaces/:id/:section" element={<SpacesPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/:section" element={<SettingsPage />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="admin/:tab" element={<AdminPage />} />
          <Route path="messages" element={<MessagesRoute />} />
          <Route path="messages/new" element={<MessagesRoute />} />
          <Route path="messages/:id" element={<ConversationRoute />} />
          <Route path="join" element={<JoinGroupPage />} />
          <Route path="space-join" element={<JoinSpacePage />} />
          <Route path="starred" element={<StarredPage />} />
          <Route path="calls" element={<CallsPage />} />
          <Route path="discover" element={<DiscoverRoute />} />
          <Route path="discover/:section" element={<DiscoverRoute />} />
          <Route path="calendar" element={<CalendarRoute />} />
          <Route path="calendar/:tab" element={<CalendarRoute />} />
          <Route path="learn" element={<LearnRoute />} />
          <Route path="learn/:section" element={<LearnRoute />} />
          <Route path="learn/class/:classId" element={<LearnRoute />} />
          <Route path="edu-join" element={<JoinEduPage />} />
          <Route path="ai" element={<AiPage />} />
          <Route path="link" element={<LinkDevicePage />} />
          <Route path="*" element={<Navigate to="/spaces" replace />} />
        </Route>
      </Routes>
    </PageBoundary>
  );
}

startInstallWatch();
prefetchPages();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppProvider>
      <ChatProvider>
        <BrowserRouter>
          <ScrollReset />
          <App />
        </BrowserRouter>
      </ChatProvider>
    </AppProvider>
  </StrictMode>,
);
